import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';

/** What a skeleton row leads with: a coin, a market's square, an article's picture. */
type SkeletonThumb = 'coin' | 'square' | 'wide';

/** The default skeleton is the panel's own surface; the bars take the next shade. */
const BAR = 'bg-v2-neutral-surface dark:bg-v2-surface-tertiary';

const THUMB_CLASS: Record<SkeletonThumb, string> = {
  coin: 'size-6 rounded-full',
  square: 'size-7 rounded-[6px]',
  wide: 'h-7 w-10 rounded-[5px]',
};

interface SkeletonRowsProps {
  thumb: SkeletonThumb;
  count?: number;
}

/** A section's rows while they load, the size of the rows to come. */
export function SkeletonRows({ thumb, count = 3 }: SkeletonRowsProps) {
  return Array.from({ length: count }, (_, i) => (
    <li key={i} aria-hidden className="flex h-10 items-center gap-2.5 px-2">
      <Skeleton className={cn('shrink-0', BAR, THUMB_CLASS[thumb])} />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton className={cn('h-2.5 w-3/5 rounded-full', BAR)} />
        <Skeleton className={cn('h-2 w-16 rounded-full', BAR)} />
      </div>
      {thumb === 'wide' ? null : (
        <div className="flex flex-col items-end gap-1.5">
          <Skeleton className={cn('h-2.5 w-12 rounded-full', BAR)} />
          <Skeleton className={cn('h-2 w-8 rounded-full', BAR)} />
        </div>
      )}
    </li>
  ));
}

/** A section's caption bar and its rows while they load. */
function SkeletonSection({ thumb, count }: SkeletonRowsProps) {
  return (
    <div className="flex flex-col">
      <div className="flex h-[26px] items-center pl-2">
        <Skeleton className={cn('h-2 w-16 rounded-full', BAR)} />
      </div>
      <ul className="flex flex-col">
        <SkeletonRows thumb={thumb} count={count} />
      </ul>
    </div>
  );
}

/** The whole list while the first answer to a query is out. */
export function SearchSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-1.5">
      <SkeletonSection thumb="coin" />
      <SkeletonSection thumb="square" count={2} />
    </div>
  );
}
